import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import last from 'lodash/last';
import isNil from 'lodash/isNil';

import { formatDate, parseDate } from '@common/utils/date';
import moment from 'moment';
import Dropdown from '@common/containers/fields/Dropdown';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { isString, isNumber } from '@common/utils/string';
import { Input } from './components/input';
import { Checkbox } from './components/checkbox';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Dropdown as FilterDropdown } from './components/dropdown';
import { NewTableColumnFilter, NewTableColumnFilterOption, NewTableFilterApplyType, NewTableFilterName, NewTableFilterType, NewTableFilterValue, NewTableFilters } from '../../new-table.types';
import { MessageProps } from '@common/components/MessageBox.types';
import { ErrorMessage } from '@common/components/error-message';
import { getTestAttributes } from '@common/utils/tests';
import { FolderFilter } from './components/folder/Folder';
import { LookupTableFilter } from './components/lookup-table';
import { HyperlinkEditor } from '@common/components/hyperlink/HyperlinkEditor';
import { UploadModal } from '@common/components/upload-modal/UploadModal';
import { ImageUploadInput } from '@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/components/body/components/row/components/cell/ImageUploadInput';

import './filters.styles.scss';

export type FiltersProps = NewTableColumnFilter & {
	showMessage: (props: MessageProps) => void;
	defaultValue?: NewTableFilterValue;
	moduleName: string;
	filters: NewTableFilters['operators'];
	filterName?: string | number;
	onClose: () => void;
	onApply: (value?: NewTableFilterValue) => void;
	getFolders?: () => Promise<Array<any>>; // Add folder loading function
};

enum FilterDtoProperties {
	whereClause1 = 'whereClause1',
	operator1 = 'operator1',
	whereClause2 = 'whereClause2',
	operator2 = 'operator2',
}

type FiltersState = {
	value: NewTableFilterValue;
	showUploadModal?: boolean;
	    uploadTargetProperty?: FilterDtoProperties.whereClause1 | FilterDtoProperties.whereClause2;
	    uploadAccept?: Array<string>;
	    uploadTitle?: string;
};

type RenderFieldMetadata = {
	moduleName: string;
	type: NewTableFilterType;
	className?: string;
	value?: string | number;
	metadata: {
		property: string;
	};
	onSave: (params: { value?: string | number; metadata: { property: string } }) => void;
};

export class Filters extends React.Component<FiltersProps, FiltersState> {
	private andORPropertyName = 'andOR';
	static defaultProps = {
		defaultValue: {},
		type: NewTableFilterType.text,
		outputDateFormat: 'MM/DD/YYYY',
	};

	state = {
		value: this.props.defaultValue || {},
        showUploadModal: false,
        uploadTargetProperty: undefined,
        uploadAccept: undefined,
        uploadTitle: undefined,
	};

    private processors: Partial<Record<NewTableFilterType, (x: string | number) => string | number>> = {
		[NewTableFilterType.date]: (x: string | number) => {
			if (x) {
				return formatDate(parseDate(x, this.props.outputDateFormat), { format: 'YYYY-MM-DD' });
			}

			return x;
		}
    };

	private normalizeNumberBeforeSave = (value: string) => {
		if (last(value) === '.') {
			value = value.trim().slice(0, -1);
		}

		return value;
	}

	private handleApply = () => {
		const { onApply, showMessage, filters } = this.props;
		const defaultAndORValue = undefined;
		const processedValue = this.state.value
			? {
				[this.andORPropertyName]: defaultAndORValue,
				...this.state.value,
			}
			: undefined;

		const isValidValue = (operator?: string | number, value?: any) => {
			if (operator && filters[operator]?.isValidValue) {
				const result = filters[operator]?.isValidValue!(value);

				return result;
			}

			return !isNil(value) && value !== "";
		};

		const validators = [
			{
				isValid: () => {
					return (
						(!isNil(processedValue?.operator1) && isValidValue(processedValue?.operator1, processedValue?.whereClause1)) ||
						(!isNil(processedValue?.operator2) && isValidValue(processedValue?.operator2, processedValue?.whereClause2))
					);
				},
				message: I18n.t('Please select at least one operator and one value'),
			},
			{
				isValid: () => {
					const parts = [
						[processedValue?.operator1, processedValue?.whereClause1],
						[processedValue?.operator2, processedValue?.whereClause2],
					] as const;

					const valid = parts.every(([operator, value]) => {
						const bothEntered = !isNil(operator) && isValidValue(operator, value);
						const bothMissed = isNil(operator) && isNil(value);

						return bothEntered || bothMissed;
					});

					return valid;
				},
				message: I18n.t('Both operator and value should be selected'),
			},
			{
				isValid: () => {
					const allValuesSelected =
						!isNil(processedValue?.operator1) &&
						isValidValue(processedValue?.operator1, processedValue?.whereClause1) &&
						!isNil(processedValue?.operator2) &&
						isValidValue(processedValue?.operator2, processedValue?.whereClause2);

					return allValuesSelected ? !isNil(processedValue?.andOR) : true;
				},
				message: I18n.t('Please select And/Or'),
			},
		];

		const error = validators.find((x) => !x.isValid())?.message;

		if (error) {
			showMessage({
				header: I18n.t('Operation canceled'),
				content: <ErrorMessage>{error}</ErrorMessage>,
				size: 'small',
				confirmText: I18n.t('Ok')
			});

			return;
		}

		onApply(processedValue);
	}

	private renderFilterControls = (
		{
			typeProperty,
			textProperty,
			typeElementId,
			valueContainerElementId,
		}: {
			typeElementId: string;
			valueContainerElementId: string;
			typeProperty: FilterDtoProperties.operator1 | FilterDtoProperties.operator2;
			textProperty: FilterDtoProperties.whereClause1 | FilterDtoProperties.whereClause2;
		}) => {
		const { filters, type } = this.props;
		const { value } = this.state;
		const filterTypeValue = isNil(value[typeProperty]) ? undefined : filters[value[typeProperty]!]?.name;
		const processor = this.processors[type] || ((x: any) => x);
		const currentFilterValue = processor(value[textProperty] ?? '');

		return (
			<Flex alignItems="center" justify="space-between" style={{width:"100%"}}>
				<Dropdown
					className="NewTableFilters__filter-type"
					{...getTestAttributes({ id: typeElementId })}
					selectedValue={filterTypeValue ?? 'equals'}
					searchable={false}
					options={Object.values(filters).map((x) => x.name)}
					optionsHeight={100} fullWidth={true}
					onValueChanged={this.handleFilterTypeChange(typeProperty)}
				/>

				<div {...getTestAttributes({ id: valueContainerElementId })} className="NewTableFilters__filter-value">
					{this.renderField(
						{
							type,
							moduleName: this.props.moduleName,
							className: "NewTableFilters__filter-text",
							value: currentFilterValue,
							metadata: {
								property: textProperty,
							},
							onSave: this.handleFilterChange,
						}
					)}
				</div>
			</Flex>
		);
	}

	private handleFilterTypeChange = (property: FilterDtoProperties.operator1 | FilterDtoProperties.operator2) => (filterName: NewTableFilterName) => {
		const { filters } = this.props;

		const filterTypeId = Object.keys(filters).find((id) => {
			return filters[id]?.name === filterName;
		});

		if (!isNil(filterTypeId)) {
			this.changeValue(property, filterTypeId);
		}
	}

	private handleFilterChange: RenderFieldMetadata['onSave'] = ({ value, metadata: { property } }) => {
        if (this.props.type === NewTableFilterType.date) {
			value = formatDate(value, { format: this.props.outputDateFormat });
        }

		// If user enters value for clause2 but hasn't chosen an operator yet,
		// default operator2 to "equals" so validation can pass.
		if (property === FilterDtoProperties.whereClause2 && isNil(this.state.value.operator2)) {
			const equalsId = Object.keys(this.props.filters).find((id) => this.props.filters[id]?.name === 'equals') ?? '1';
			this.setState((state) => ({
				...state,
				value: {
					...state.value,
					[FilterDtoProperties.operator2]: equalsId,
					[property]: value,
				}
			}));
			return;
		}

		// Similarly ensure clause1 has a default operator if user types value first
		if (property === FilterDtoProperties.whereClause1 && isNil(this.state.value.operator1)) {
			const equalsId = Object.keys(this.props.filters).find((id) => this.props.filters[id]?.name === 'equals') ?? '1';
			this.setState((state) => ({
				...state,
				value: {
					...state.value,
					[FilterDtoProperties.operator1]: equalsId,
					[property]: value,
				}
			}));
			return;
		}

		this.changeValue(property, value);
	}

	private changeValue = (property: string, value?: string | number) => {
		this.setState((state) => ({
			...state,
			value: {
				...state.value,
				[property]: value
			}
		}));
	}

	private handleApplyTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.persist();

		const { value } = e.target;

		this.changeValue(this.andORPropertyName, value);
	}

	private get applyTypes() {
		return [
			{ label: 'And', id: NewTableFilterApplyType.and, inputProps: getTestAttributes({ id: 'radio-grid-filter-and-input' }), labelProps: getTestAttributes({ id: 'radio-grid-filter-and-label' }) },
			{ label: 'Or', id: NewTableFilterApplyType.or, inputProps: getTestAttributes({ id: 'radio-grid-filter-or-input' }), labelProps: getTestAttributes({ id: 'radio-grid-filter-or-label' }) },
		];
	}

	private getFieldConfig = ({ type, options }: { type: NewTableFilterType; options?: Array<NewTableColumnFilterOption> }) => {

		const checkboxCanHaveOptions = type != NewTableFilterType.checkbox;

		if (options?.length && options.length > 0 && checkboxCanHaveOptions) {
			return {
				render: this.renderDropdown,
			};
		}

		const config = {
			[NewTableFilterType.text]: {
				render: this.renderInput
			},
			[NewTableFilterType.date]: {
				render: this.renderDate
			},
			[NewTableFilterType.number]: {
				render: this.renderNumber
			},
			[NewTableFilterType.integer]: {
				render: this.renderInteger
			},
			[NewTableFilterType.checkbox]: {
				render: this.renderCheckbox
			},
			[NewTableFilterType.dropdown]: {
				render: this.renderDropdown
			},
			[NewTableFilterType.folder]: {
				render: this.renderFolder
			},
			[NewTableFilterType.image]: {
				render: this.renderImage
			},
			[NewTableFilterType.lookupTable]: {
				render: this.renderLookupTable
			},
			[NewTableFilterType.table]: {
				render: this.renderInput
			},
			[NewTableFilterType.computed]: {
				render: this.renderInput
			},
			[NewTableFilterType.summary]: {
				render: this.renderInput
			},
			[NewTableFilterType.tableSummary]: {
				render: this.renderInput
			},
			[NewTableFilterType.hyperlink]: {
				render: this.renderHyperlink
			},
		};

		return config[type];
	}

	private renderNumber = (data: RenderFieldMetadata) => {
		return (
			<Input
				normalizeBeforeSave={this.normalizeNumberBeforeSave}
				validate={isNumber}
				{...data}
				type="number"
			/>
		);
	}

	private isInteger = (v: string) => {
		return Number.isInteger(Number(v));
	}

	private renderInteger = (data: RenderFieldMetadata) => {
		return (
			<Input
				normalizeBeforeSave={this.normalizeNumberBeforeSave}
				validate={this.isInteger}
				{...data}
				type="number"
			/>
		);
	}

	private renderInput = (data: RenderFieldMetadata) => {
		return <Input {...data} />;
	}

    private renderDate = (data: RenderFieldMetadata) => {
        const valueStr = (data.value as string) || '';
        const m = valueStr ? moment(valueStr, ['MM/DD/YYYY','YYYY-MM-DD'], true) : null;
        const selected = m && m.isValid() ? m.toDate() : null;

        return (
            <div style={{ width: '100%' }}>
                <DatePicker
                    placeholderText='MM/DD/YYYY'
                    selected={selected}
                    onChange={(date: Date | null) => {
                    const formatted = date ? moment(date).format('MM/DD/YYYY') : '';
                    data.onSave({ value: formatted, metadata: data.metadata });
                }}
                    dateFormat={'MM/dd/yyyy'}
                    isClearable
                    wrapperClassName="NewTableFilters__datepicker"
                />
            </div>
        );
    }

	private renderHyperlink = (data: RenderFieldMetadata) => {
		// Wire HyperlinkEditor to filter value lifecycle
		const handleChange = (val: string) => {
			data.onSave({ value: val, metadata: data.metadata });
		};

		return (
			<HyperlinkEditor
				style={{paddingLeft:"4px"}}
				value={(data.value as string) ?? ''}
				onChange={handleChange}
				onUpload={() => this.setState({ 
					showUploadModal: true, 
					uploadTargetProperty: data.metadata.property as FilterDtoProperties.whereClause1 | FilterDtoProperties.whereClause2,
					uploadAccept: [".pdf", ".doc", ".docx", ".txt", ".jpg", ".png", ".gif", ".xls", ".xlsx"],
					uploadTitle: 'Upload Document'
				})}
				autoFocus={false}
			/>
		);
	}

	private renderCheckbox = (data: RenderFieldMetadata) => {
		return <Checkbox {...data} />;
	}

	private renderDropdown = (data: RenderFieldMetadata) => {
		return this.props.options && <FilterDropdown options={this.props.options} {...data} />;
	}


	private renderFolder = (data: RenderFieldMetadata) => {
		return <FolderFilter {...data} getFolders={this.props.getFolders} />;
	}

	private renderLookupTable = (data: RenderFieldMetadata) => {
		return <LookupTableFilter {...data} lookupValues={this.props.lookupValues || {}} />;
	}

	private renderImage = (data: RenderFieldMetadata) => {
		// Keep value lifecycle consistent with other inputs
		const text = (data.value as string) ?? '';
		return (
			<ImageUploadInput
				value={text}
				onChange={(val: string) => data.onSave({ value: val, metadata: data.metadata })}
				onUpload={() => this.setState({ 
					showUploadModal: true,
					uploadTargetProperty: data.metadata.property as FilterDtoProperties.whereClause1 | FilterDtoProperties.whereClause2,
					uploadAccept: [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"],
					uploadTitle: 'Upload Image'
				})}
				style={{ backgroundColor: "white",paddingLeft:"4px" }}
			/>
		);
	}

	private renderField = (data: RenderFieldMetadata) => {
		const fieldConfig = this.getFieldConfig({ type: data.type, options: this.props.options });
		const render = fieldConfig ? fieldConfig.render : (() => null);

		return render(data);
	}

	componentDidMount() {
		if (!this.props.moduleName) {
			console.error('React Table: provide moduleName property for unique identifiers');
		}

		// Ensure a default operator ("equals") is selected when none is provided,
		// so applying a value works even if the user doesn't touch the operator.
		const equalsId = Object.keys(this.props.filters).find((id) => this.props.filters[id]?.name === 'equals') ?? '1';
		this.setState((state) => ({
			...state,
			value: {
				...state.value,
				operator1: state.value.operator1 ?? equalsId,
				operator2: state.value.operator2 ?? state.value.operator2, // leave second operator unchanged unless user sets it
			},
		}));
	}

	render() {
		const { onClose, filterName } = this.props;
		const { value } = this.state;

		return (
			<Modal
				show
				animation={false}
				backdrop="static"
				{...{ bsClass: "bootstrap modal" }}
				dialogClassName="NewTableFilters"
				{...{ onEscapeKeyDown: onClose }}
				onHide={onClose}
			>
				<ModalHeader closeButton onHide={onClose}>
					<ModalTitle id="msg-box-header">
						<span className="fa fa-filter" /> {I18n.t('Filter Column')}
					</ModalTitle>
				</ModalHeader>

				<ModalBody>
                    <Flex className="NewTableFilters__main-control" justify="space-between" width="100%" alignItems="center">
						<span>{I18n.t('Show rows where')}{(isNumber(filterName) || isString(filterName)) ? ` ${filterName}` : ''}:</span>

						<Button id="btn-grid-filter-save" className="btn-primary" onClick={this.handleApply}>
							{I18n.t('Filter')}
						</Button>
					</Flex>

                    {this.renderFilterControls(
						{
							textProperty: FilterDtoProperties.whereClause1,
							typeProperty: FilterDtoProperties.operator1,
							typeElementId: 'lbx-grid-filter-operator',
							valueContainerElementId: 'pan-grid-filter-where1',
						}
					)}

					<Flex alignItems="center" className="NewTableFilters__filter-apply-types">
						{this.applyTypes.map((type) => (
							<React.Fragment key={type.id}>
								<input
									{...type.inputProps}
									name="Table-apply-type"
									type="radio"
									className="NewTableFilters__filter-apply-types__input"
									value={type.id}
									checked={type.id === value.andOR}
									onChange={this.handleApplyTypeChange}
								/>
								<label
									{...type.labelProps}
									htmlFor={type.inputProps.id}
									className="NewTableFilters__filter-apply-types__label"
								>
									{I18n.t(type.label)}
								</label>
							</React.Fragment>
						))}
					</Flex>

                    {this.renderFilterControls(
						{
							textProperty: FilterDtoProperties.whereClause2,
							typeProperty: FilterDtoProperties.operator2,
							typeElementId: 'lbx-grid-filter-operator2',
							valueContainerElementId: 'pan-grid-filter-where2',
						}
					)}

					<UploadModal
						isOpen={!!this.state.showUploadModal}
						onClose={() => this.setState({ showUploadModal: false })}
						onUpload={(file: any) => {
							const fileName = file && file.name ? file.name : undefined;
							const targetProp = this.state.uploadTargetProperty;
							if (fileName && targetProp) {
								// Set uploaded filename
								this.changeValue(targetProp, fileName);
								// Ensure corresponding operator is set to equals by default if missing
								const equalsId = Object.keys(this.props.filters).find((id) => this.props.filters[id]?.name === 'equals') ?? '1';
								if (targetProp === FilterDtoProperties.whereClause1 && isNil(this.state.value.operator1)) {
									this.changeValue(FilterDtoProperties.operator1, equalsId);
								}
								if (targetProp === FilterDtoProperties.whereClause2 && isNil(this.state.value.operator2)) {
									this.changeValue(FilterDtoProperties.operator2, equalsId);
								}
							}
							this.setState({ showUploadModal: false, uploadTargetProperty: undefined, uploadAccept: undefined, uploadTitle: undefined });
						}}
						onReject={() => this.setState({ showUploadModal: false, uploadAccept: undefined, uploadTitle: undefined })}
						title={this.state.uploadTitle || 'Upload Document'}
						accept={this.state.uploadAccept || [".pdf", ".doc", ".docx", ".txt", ".jpg", ".png", ".gif", ".xls", ".xlsx"]}
						maxSize={10 * 1024 * 1024}
						multiple={false}
					/>
				</ModalBody>
			</Modal>
		);
	}
}
